import { CommonModule } from '@angular/common';
import { Component, computed, Input, Output, EventEmitter, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ButtonComponent } from '../button/button.component';
import { OutsideClickDirective } from '../../directives/outside-click.directive';
import { LabelService } from '../../services/label.service';
import * as Types from '../../shared/types';
import { Dropdown, LabelTypes } from '../../shared/label.types';
import { FilterService } from '../../services/filter.service';
import { FilterItemComponent } from './filter-item/filter-item.component';
import { FilterClusterComponent } from './filter-cluster/filter-cluster.component';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { SharedDataService } from '../../services/shared-data.service';
import { TableFilterComponent } from './table-filter/table-filter.component';
import { FilterSaveDropdownComponent } from './filter-save-dropdown/filter-save-dropdown.component';
import { CommonService } from '../../services/common.service';
import { FilterButtonComponent } from './filter-button/filter-button.component';
import {
  ConceptFilterDialogComponent,
  ConceptFilterDialogResult,
} from './concept-filter-dialog/concept-filter-dialog.component';
import { SavedConceptsComponent } from './saved-concepts/saved-concepts.component';

@Component({
  selector: 'filter-section',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    OutsideClickDirective,
    DragDropModule,
    FilterItemComponent,
    FilterClusterComponent,
    TableFilterComponent,
    FilterSaveDropdownComponent,
    FilterButtonComponent,
    MatDialogModule,
    SavedConceptsComponent,
  ],
  templateUrl: './filter-section.component.html',
  styleUrl: './filter-section.component.scss',
})
export class FilterSectionComponent {
  constructor(
    public labelService: LabelService,
    private filterService: FilterService,
    private sharedDataService: SharedDataService,
    private commonService: CommonService,
    private dialog: MatDialog
  ) {}

  /** When true, section is embedded (e.g. in chart form): no button, panel always open, no global toggle/refresh. */
  @Input() embedded: boolean = false;
  /** When true, dropdown opens upward (e.g. for NOVA input at bottom of screen). */
  @Input() openUpward: boolean = false;
  @Output() filtersApplied = new EventEmitter<Types.FilterCondition[]>();

  @Input() buttonTitle: string = '';
  @ViewChild(SavedConceptsComponent) savedConceptsRef?: SavedConceptsComponent;

  protected _filterDataDisplayed = computed<boolean>(() => this.filterService.filterDataDisplayedStatus());
  protected _dropdownOpened = signal<boolean>(false);
  dropdown: Dropdown = {} as Dropdown;
  filterData: Array<Types.FilterCondition> = [];
  applyButtonActive: boolean = false;
  /** Prevents outside-click from closing the filter panel while concept dialog/nested dropdown is open. */
  private suppressOutsideClose = false;

  ngOnInit() {
    this.labelService.getLabelData().subscribe((data: LabelTypes) => {
      this.dropdown = data?.dropdown ?? {};
    });

    // Initialize temp data with actual data
    this.filterService.resetTempFiltersToActual();

    // Subscribe to temporary filter data for display
    this.filterService.getTempFilterCondition().subscribe((data) => {
      this.filterData = [...data];
    });

    this.filterService.getApplyButtonStatus().subscribe((status) => {
      this.applyButtonActive = status;
    });

    if (this.embedded) {
      this._dropdownOpened.set(true);
      this.getFilterList();
    } else if (this.openUpward) {
      // NOVA: use local state only — no linkage to insights dashboard toggle
      this.getFilterList();
    } else {
      this.sharedDataService.getToggleValues().subscribe((values) => {
        this._dropdownOpened.set(values.filter);
      });
    }
  }

  getFilterList() {
    this.commonService.getFiltersList({}).subscribe((data) => {
      if (data && data.length) {
        this.filterService.setSavedFilterList(data);
      }
    });
  }

  toggleDropdown = () => {
    if (this.embedded) return;
    if (!this._dropdownOpened()) {
      // Opening filter panel - reset temp data to current applied filters
      this.filterService.resetTempFiltersToActual();
      this.getFilterList();
    }
    this.getFilterConditionDataValue();
    if (this.openUpward) {
      // NOVA: local toggle only — independent of insights dashboard
      this._dropdownOpened.update((v) => !v);
    } else {
      this.sharedDataService.toggleFilter(!this._dropdownOpened());
    }
  };

  getFilterConditionDataValue = () => {
    const tempData = this.filterService.getTempFilterConditionDataValue();
    this.filterData = [...tempData];
  };

  handleAddCondition = () => {
    this.filterService.addNewCondition();
  };

  onOutsideClick = () => {
    if (this.embedded || this.suppressOutsideClose) return;
    // When closing without applying, reset temp data to actual filters
    this.filterService.resetTempFiltersToActual();
    if (this.openUpward) {
      // NOVA: close locally only
      this._dropdownOpened.set(false);
    } else {
      this.sharedDataService.toggleFilter(false);
    }
  };

  handleClearFilters = () => {
    this.filterService.clearFilters();
    this.getFilterConditionDataValue();
  };

  handleApplyFilters = () => {
    if (this.applyButtonActive) {
      this.getFilterConditionDataValue();
      // Apply temporary filters to actual filters
      this.filterService.applyTempFiltersToActual();
      if (this.embedded) {
        this.filtersApplied.emit(this.filterService.getTempFilterConditionDataValue());
      } else if (this.openUpward) {
        // NOVA: close panel locally only — do not touch dashboard state/refresh
        this._dropdownOpened.set(false);
      } else {
        this.filterService.setFilterDataDisplayedStatus(true);
        this.filterService.setRefreshDataStatus(true);
        this.sharedDataService.toggleFilter(false);
        this.filtersApplied.emit(this.filterService.getFilterConditionDataValue());
      }
    }
  };

  drop(event: CdkDragDrop<Types.FilterCondition[]>) {
    moveItemInArray(this.filterData, event.previousIndex, event.currentIndex);
    if (event.previousIndex !== event.currentIndex) {
      this.filterService.reIndexArrayAfterDragDrop(this.filterData);
    }
  }
  toggleMoreDropdown() {}

  get isScrollable(): boolean {
    return this.filterData.length > 5;
  }

  openConceptFilterDialog(): void {
    this.suppressOutsideClose = true;
    const dialogRef = this.dialog.open(ConceptFilterDialogComponent, {
      autoFocus: false,
      width: 'auto',
      maxWidth: '90vw',
      maxHeight: '90vh',
      panelClass: 'concept-filter-dialog-panel',
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((result: ConceptFilterDialogResult | undefined) => {
      this.suppressOutsideClose = false;
      this.savedConceptsRef?.loadConcepts();
      if (!result?.filters?.length) {
        return;
      }
      this.filterService.injectConceptFilters(result.filters, result.combineWith || 'OR');
      this.getFilterConditionDataValue();
    });
  }

  onSavedConceptSelected(concept: Types.SavedConceptFilter): void {
    const filters = concept.filters ?? [];
    if (!filters.length) {
      return;
    }
    const combineWith = (concept.combineWith as 'OR' | 'AND') || 'OR';
    this.filterService.injectConceptFilters(filters, combineWith);
    this.getFilterConditionDataValue();
  }

  onSavedConceptsDropdownToggled(opened: boolean): void {
    this.suppressOutsideClose = opened;
  }
}
